import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  ArrowLeft,
  User,
  Package,
  Calendar,
  DollarSign,
  FileText,
  Clock,
  Plus,
  Minus,
  BookOpen,
  CreditCard,
  ChevronDown,
  ChevronUp,
  AlertCircle,
  CheckCircle,
  XCircle,
  Pencil,
  X as XIcon,
  List,
  BarChart3,
  CalendarDays,
  TrendingUp,
  Calculator
} from 'lucide-react';
import api from '../utils/api';
import { formatDuration, formatHours } from '../utils/timeFormatting';

interface Subject {
  id: string;
  name: string;
  category: string;
}

interface HourPackage {
  id: string;
  subjectId: string;
  studentId: string;
  totalHours: number;
  usedHours: number;
  remainingHours: number;
  packageType: string;
  purchasePrice: number;
  pricePerHour: number;
  discountPercent: number;
  status: 'ACTIVE' | 'PENDING' | 'EXPIRED' | 'DEPLETED';
  purchasedAt: string;
  subject: {
    id: string;
    name: string;
    category: string;
  };
  transactions?: PackageTransaction[];
}

interface PackageTransaction {
  id: string;
  packageId: string;
  transactionType: string;
  hoursChanged: number;
  description?: string;
  performedBy?: string;
  createdAt: string;
}

interface PackagePreviewExactLesson {
  transactionId: string;
  bookingId: string;
  title: string | null;
  type: 'LESSON' | 'CONSULTATION';
  status: string;
  startTime: string;
  endTime: string;
  actualHours: number;
  recordedHoursChanged: number;
}

interface PackagePreviewExactAdjustment {
  transactionId: string;
  hoursChanged: number;
  description: string | null;
  createdAt: string;
}

interface PackagePreviewExact {
  packageId: string;
  totalHours: number;
  stored: { usedHours: number; remainingHours: number };
  exact: {
    usedHoursFromLessons: number;
    adminAdjustmentHours: number;
    usedHours: number;
    remainingHours: number;
  };
  discrepancy: { usedHours: number; remainingHours: number };
  lessons: PackagePreviewExactLesson[];
  adminAdjustments: PackagePreviewExactAdjustment[];
}

interface Booking {
  id: string;
  startTime: string;
  endTime: string;
  type: 'LESSON' | 'CONSULTATION';
  status: string;
  price: number;
  isOnline: boolean;
  student: {
    id: string;
    name: string;
    email: string;
  };
  subject: {
    name: string;
    category: string;
  };
  payments?: Payment[];
  packageTransaction?: {
    hoursChanged: number;
  };
}

interface Payment {
  id: string;
  amount: number;
  status: string;
  paidAt?: string;
}

interface Homework {
  id: string;
  title: string;
  description?: string;
  dueDate: string;
  status: string;
  subject: {
    name: string;
  };
  createdAt: string;
}

interface Resource {
  id: string;
  title: string;
  description?: string;
  fileExtension?: string;
  createdAt: string;
}

interface StudentData {
  id: string;
  name: string;
  email: string;
  phoneNumber?: string;
  createdAt: string;
  studentProfile?: {
    description?: string;
    hasHadConsultation?: boolean;
    consultationEnabled?: boolean;
    subjects: Subject[];
  };
}

interface FinancialSummary {
  totalPaid: number;
  totalOwed: number;
  totalLessons: number;
  totalPackagesPurchased: number;
}

const StudentDetailPage: React.FC = () => {
  const { studentId } = useParams<{ studentId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [student, setStudent] = useState<StudentData | null>(null);
  const [hourPackages, setHourPackages] = useState<HourPackage[]>([]);
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [homework, setHomework] = useState<Homework[]>([]);
  const [financialSummary, setFinancialSummary] = useState<FinancialSummary>({
    totalPaid: 0,
    totalOwed: 0,
    totalLessons: 0,
    totalPackagesPurchased: 0
  });
  
  const [loading, setLoading] = useState(true);
  const [showHomeworkDropdown, setShowHomeworkDropdown] = useState(false);
  const [adjustingPackage, setAdjustingPackage] = useState<string | null>(null);
  const [adjustmentReason, setAdjustmentReason] = useState('');
  const [previewExactPkg, setPreviewExactPkg] = useState<HourPackage | null>(null);
  const [previewExactData, setPreviewExactData] = useState<PackagePreviewExact | null>(null);
  const [previewExactLoading, setPreviewExactLoading] = useState(false);
  const [previewExactError, setPreviewExactError] = useState<string | null>(null);

  const openPreviewExact = async (pkg: HourPackage) => {
    setPreviewExactPkg(pkg);
    setPreviewExactData(null);
    setPreviewExactError(null);
    setPreviewExactLoading(true);
    try {
      const response = await api.get(`/hour-packages/${pkg.id}/preview-exact`);
      setPreviewExactData(response.data);
    } catch (err: any) {
      setPreviewExactError(err?.response?.data?.message || 'Failed to load preview');
    } finally {
      setPreviewExactLoading(false);
    }
  };

  const closePreviewExact = () => {
    setPreviewExactPkg(null);
    setPreviewExactData(null);
    setPreviewExactError(null);
    setPreviewExactLoading(false);
  };
  const [showDeniedCancelled, setShowDeniedCancelled] = useState(false);
  const [historyView, setHistoryView] = useState<'list' | 'subjects' | 'calendar' | 'trends'>('list');
  const [calendarOffset, setCalendarOffset] = useState(0);
  const [trendsOffset, setTrendsOffset] = useState(0);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [listSubjectFilter, setListSubjectFilter] = useState<string>('ALL');
  const [listModeFilter, setListModeFilter] = useState<'ALL' | 'ONLINE' | 'IN_PERSON'>('ALL');
  const [listTypeFilter, setListTypeFilter] = useState<'ALL' | 'LESSON' | 'CONSULTATION'>('ALL');
  const [lessonResources, setLessonResources] = useState<Resource[]>([]);
  const [showAllHomework, setShowAllHomework] = useState(false);
  const [showAllResources, setShowAllResources] = useState(false);

  // Edit-student modal state. allSubjects is fetched lazily on first open
  // so it doesn't slow down the main page load.
  const [editOpen, setEditOpen] = useState(false);
  const [editName, setEditName] = useState('');
  const [editSubjectIds, setEditSubjectIds] = useState<Set<string>>(new Set());
  const [allSubjects, setAllSubjects] = useState<Subject[]>([]);
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);

  useEffect(() => {
    if (user?.role === 'TUTOR' && studentId) {
      fetchStudentData();
    }
  }, [user, studentId]);

  const fetchStudentData = async () => {
    try {
      setLoading(true);
      
      // Fetch all data in parallel
      const [
        studentRes,
        packagesRes,
        bookingsRes,
        homeworkRes,
        statsRes
      ] = await Promise.all([
        api.get(`/admin/students/${studentId}`),
        api.get(`/hour-packages/student/${studentId}`)
          .catch(() => ({ data: [] })),
        api.get(`/bookings/${studentId}`),
        api.get(`/homework/student/${studentId}`),
        api.get(`/admin/students/${studentId}/stats`)
      ]);

      setStudent(studentRes.data);
      setHourPackages(packagesRes.data || []);
      
      // Filter bookings to only show this student's bookings
      const studentBookings = bookingsRes.data.filter((b: Booking) => 
        b.student?.id === studentId || 
        (bookingsRes.data.length > 0 && !b.student) // Handle legacy bookings
      );
      setBookings(studentBookings);

      // Extract lesson resources from bookings
      const allLessonResources: Resource[] = [];
      studentBookings.forEach((booking: any) => {
        if (booking.lessonResources) {
          booking.lessonResources.forEach((lr: any) => {
            if (lr.resource) {
              allLessonResources.push(lr.resource);
            }
          });
        }
      });
      setLessonResources(allLessonResources);
      
      setHomework(homeworkRes.data || []);
      
      // Calculate financial summary
      const stats = statsRes.data;
      setFinancialSummary({
        totalPaid: stats.totalPaid || 0,
        totalOwed: stats.totalOwed || 0,
        totalLessons: stats.totalBookings || 0,
        totalPackagesPurchased: stats.totalPackagesPurchased || 0
      });
      
    } catch (error) {
      console.error('Failed to fetch student data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAdjustHours = async (packageId: string, hoursChange: number) => {
    if (!adjustmentReason.trim()) {
      alert('Please provide a reason for the adjustment');
      return;
    }

    try {
      await api.post(`/hour-packages/${packageId}/adjust`, {
        hoursChange,
        description: adjustmentReason
      });
      
      // Refresh data
      await fetchStudentData();
      setAdjustingPackage(null);
      setAdjustmentReason('');
      alert('Hours adjusted successfully');
    } catch (error) {
      console.error('Failed to adjust hours:', error);
      alert('Failed to adjust hours. Please try again.');
    }
  };

  const handleToggleConsultation = async (currentState: boolean) => {
    try {
      await api.put(`/admin/students/${studentId}/consultation`, {
        consultationEnabled: !currentState
      });
      await fetchStudentData();
    } catch (error: any) {
      console.error('Failed to update consultation status:', error);
      alert(error.response?.data?.message || 'Failed to update consultation status');
    }
  };

  // Open the edit modal — seed the form with the student's current name +
  // subjects and lazy-fetch the master subject list on first open.
  const openEditModal = async () => {
    if (!student) return;
    setEditName(student.name);
    setEditSubjectIds(new Set((student.studentProfile?.subjects ?? []).map(s => s.id)));
    setEditError(null);
    setEditOpen(true);
    if (allSubjects.length === 0) {
      try {
        const res = await api.get('/subjects');
        setAllSubjects(Array.isArray(res.data) ? res.data : []);
      } catch (err) {
        console.error('Failed to load subjects for edit modal:', err);
        setEditError('Could not load subject list — please close and try again.');
      }
    }
  };

  const saveEdit = async () => {
    if (!studentId || !student) return;
    const trimmed = editName.trim();
    if (trimmed.length === 0) {
      setEditError('Name cannot be empty.');
      return;
    }
    setEditSaving(true);
    setEditError(null);
    try {
      // Only send fields that actually changed so a no-op edit doesn't
      // trigger e.g. a Calendar resync.
      const payload: { name?: string; subjectIds?: string[] } = {};
      if (trimmed !== student.name) payload.name = trimmed;
      const currentIds = new Set((student.studentProfile?.subjects ?? []).map(s => s.id));
      const nextIds = Array.from(editSubjectIds).sort();
      const sortedCurrent = Array.from(currentIds).sort();
      const changed = nextIds.length !== sortedCurrent.length
        || nextIds.some((id, i) => id !== sortedCurrent[i]);
      if (changed) payload.subjectIds = nextIds;

      if (Object.keys(payload).length === 0) {
        setEditOpen(false);
        return;
      }

      await api.put(`/admin/students/${studentId}`, payload);
      await fetchStudentData();
      setEditOpen(false);
    } catch (err: any) {
      console.error('Failed to update student:', err);
      setEditError(err.response?.data?.message || 'Failed to update student');
    } finally {
      setEditSaving(false);
    }
  };

  // Group subjects by category in the modal so the multi-select isn't a
  // wall of unstructured options.
  const subjectsByCategory = React.useMemo(() => {
    const groups: Record<string, Subject[]> = {};
    for (const s of allSubjects) {
      const key = s.category || 'OTHER';
      (groups[key] ||= []).push(s);
    }
    return groups;
  }, [allSubjects]);

  const categoryLabel = (key: string): string => {
    switch (key) {
      case 'ELEVEN_PLUS': return '11+';
      case 'GCSE': return 'GCSE';
      case 'A_LEVEL': return 'A-Level';
      case 'UNIVERSITY': return 'University / Oxbridge';
      default: return key;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const formatSubjectCategory = (category: string) => {
    switch (category) {
      case 'ELEVEN_PLUS':
        return '11+';
      case 'GCSE':
        return 'GCSE';
      case 'A_LEVEL':
        return 'A-Level';
      default:
        return category;
    }
  };

  const getFilteredBookings = () => {
    if (showDeniedCancelled) {
      return bookings;
    }
    return bookings.filter(booking => 
      booking.status !== 'DENIED' && booking.status !== 'CANCELLED'
    );
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ACTIVE':
      case 'CONFIRMED':
      case 'PAID':
      case 'SUBMITTED':
        return 'bg-green-100 text-green-800';
      case 'PENDING':
      case 'REQUESTED':
      case 'ASSIGNED':
        return 'bg-yellow-100 text-yellow-800';
      case 'EXPIRED':
      case 'OVERDUE':
        return 'bg-red-100 text-red-800';
      case 'DEPLETED':
      case 'CANCELLED':
      case 'DENIED':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'ACTIVE':
      case 'CONFIRMED':
      case 'PAID':
        return <CheckCircle className="w-4 h-4" />;
      case 'PENDING':
      case 'REQUESTED':
        return <Clock className="w-4 h-4" />;
      case 'EXPIRED':
      case 'OVERDUE':
      case 'DENIED':
        return <XCircle className="w-4 h-4" />;
      default:
        return <AlertCircle className="w-4 h-4" />;
    }
  };

  const calculateLessonDuration = (startTime: string, endTime: string) => {
    const start = new Date(startTime);
    const end = new Date(endTime);
    const durationMs = end.getTime() - start.getTime();
    const hours = durationMs / (1000 * 60 * 60);
    return hours;
  };

  if (user?.role !== 'TUTOR') {
    return (
      <div className="p-6">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <p className="text-yellow-800">
            <AlertCircle className="inline w-5 h-5 mr-2" />
            Only tutors can access this page.
          </p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/4"></div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="h-40 bg-gray-200 rounded"></div>
              <div className="h-60 bg-gray-200 rounded"></div>
            </div>
            <div className="h-96 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!student) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">
            <AlertCircle className="inline w-5 h-5 mr-2" />
            Student not found.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/students')}
          className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Students
        </button>
        
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center">
              <User className="w-8 h-8 mr-3 text-gray-600" />
              {student.name}
              <button
                onClick={openEditModal}
                className="ml-3 inline-flex items-center gap-1 text-sm font-medium text-gray-500 hover:text-gray-900 border border-gray-200 hover:border-gray-400 rounded-md px-2.5 py-1 transition-colors"
                title="Edit name and subjects"
              >
                <Pencil className="w-3.5 h-3.5" />
                Edit
              </button>
            </h1>
            <p className="text-gray-600 mt-1">{student.email}</p>
            {student.phoneNumber && (
              <p className="text-gray-600">{student.phoneNumber}</p>
            )}
            
            {/* Consultation Status */}
            <div className="mt-3 flex items-center gap-4">
              {student.studentProfile?.hasHadConsultation && (
                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                  <Clock className="w-3 h-3 mr-1" />
                  Had Consultation
                </span>
              )}
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">Consultation Enabled:</span>
                <button
                  onClick={() => handleToggleConsultation(student.studentProfile?.consultationEnabled ?? true)}
                  className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                    student.studentProfile?.consultationEnabled !== false 
                      ? 'bg-green-600' 
                      : 'bg-gray-300'
                  }`}
                  title={`${student.studentProfile?.consultationEnabled !== false ? 'Disable' : 'Enable'} consultation`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      student.studentProfile?.consultationEnabled !== false 
                        ? 'translate-x-4' 
                        : 'translate-x-0.5'
                    }`}
                  />
                </button>
                <span className={`text-sm font-medium ${
                  student.studentProfile?.consultationEnabled !== false 
                    ? 'text-green-600' 
                    : 'text-gray-500'
                }`}>
                  {student.studentProfile?.consultationEnabled !== false ? 'ON' : 'OFF'}
                </span>
              </div>
            </div>
          </div>
          <div className="text-right">
            <p className="text-sm text-gray-500">Member since</p>
            <p className="text-lg font-medium text-gray-900">{formatDate(student.createdAt)}</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Left Column - 2/5 width */}
        <div className="lg:col-span-2 space-y-6">
          {/* Financial Summary */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <DollarSign className="w-5 h-5 mr-2 text-gray-600" />
              Financial Summary
            </h2>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-green-50 rounded-lg p-4">
                <p className="text-sm text-green-600 font-medium">Total Paid</p>
                <p className="text-2xl font-bold text-green-900">£{financialSummary.totalPaid.toFixed(2)}</p>
              </div>
              <div className="bg-red-50 rounded-lg p-4">
                <p className="text-sm text-red-600 font-medium">Total Owed</p>
                <p className="text-2xl font-bold text-red-900">£{financialSummary.totalOwed.toFixed(2)}</p>
              </div>
              <div className="bg-blue-50 rounded-lg p-4">
                <p className="text-sm text-blue-600 font-medium">Total Lessons</p>
                <p className="text-2xl font-bold text-blue-900">{financialSummary.totalLessons}</p>
              </div>
              <div className="bg-purple-50 rounded-lg p-4">
                <p className="text-sm text-purple-600 font-medium">Packages Purchased</p>
                <p className="text-2xl font-bold text-purple-900">{financialSummary.totalPackagesPurchased}</p>
              </div>
            </div>
          </div>

          {/* Hour Packages */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <Package className="w-5 h-5 mr-2 text-gray-600" />
              Hour Packages
            </h2>
            
            {hourPackages.length === 0 ? (
              <p className="text-gray-500 text-sm">No hour packages purchased yet.</p>
            ) : (
              <div className="space-y-4">
                {hourPackages.map((pkg) => (
                  <div key={pkg.id} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <h3 className="font-medium text-gray-900">
                          {pkg.subject.name} - {formatSubjectCategory(pkg.subject.category)}
                        </h3>
                        <p className="text-sm text-gray-500">
                          Purchased {formatDate(pkg.purchasedAt)}
                        </p>
                      </div>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(pkg.status)}`}>
                        {getStatusIcon(pkg.status)}
                        <span className="ml-1">{pkg.status}</span>
                      </span>
                    </div>
                    
                    {/* Hour Balance with Adjustment Controls */}
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <p className="text-sm text-gray-600">Hour Balance</p>
                        <div className="flex items-center gap-3 mt-1">
                          <button
                            onClick={() => {
                              setAdjustingPackage(pkg.id);
                              setAdjustmentReason('Manual adjustment - removed hours');
                            }}
                            className="p-1 bg-red-100 text-red-600 hover:bg-red-200 rounded"
                            title="Remove hours"
                          >
                            <Minus className="w-4 h-4" />
                          </button>
                          
                          <span className="text-2xl font-bold text-gray-900">
                            {formatHours(pkg.remainingHours)} / {pkg.totalHours}
                          </span>
                          
                          <button
                            onClick={() => {
                              setAdjustingPackage(pkg.id);
                              setAdjustmentReason('Manual adjustment - added hours');
                            }}
                            className="p-1 bg-green-100 text-green-600 hover:bg-green-200 rounded"
                            title="Add hours"
                          >
                            <Plus className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                      
                      <div className="text-right">
                        <p className="text-sm text-gray-600">Package Value</p>
                        <p className="text-lg font-medium text-gray-900">£{pkg.purchasePrice.toFixed(2)}</p>
                        <p className="text-xs text-gray-500">£{pkg.pricePerHour.toFixed(2)}/hr</p>
                      </div>
                    </div>
                    
                    {/* Progress Bar */}
                    <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
                      <div 
                        className="bg-primary-600 h-2 rounded-full transition-all" 
                        style={{width: `${(pkg.remainingHours / pkg.totalHours) * 100}%`}}
                      ></div>
                    </div>
                    <p className="text-xs text-gray-500 text-center">
                      {formatHours(pkg.usedHours)} hours used
                    </p>

                    <div className="mt-3 flex justify-end">
                      <button
                        onClick={() => openPreviewExact(pkg)}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-indigo-50 text-indigo-700 border border-indigo-200 rounded-md hover:bg-indigo-100 transition-colors"
                        title="Show what the balance should be based on actual lesson durations (read-only, nothing is saved)"
                      >
                        <Calculator className="w-4 h-4" />
                        Preview Exact
                      </button>
                    </div>

                    {/* Adjustment Modal */}
                    {adjustingPackage === pkg.id && (
                      <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Adjustment Reason
                        </label>
                        <input
                          type="text"
                          value={adjustmentReason}
                          onChange={(e) => setAdjustmentReason(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 mb-3"
                          placeholder="Enter reason for adjustment..."
                        />
                        <div className="grid grid-cols-2 gap-2">
                          <button
                            onClick={() => handleAdjustHours(pkg.id, -1)}
                            className="bg-red-600 text-white px-3 py-2 rounded-lg hover:bg-red-700 text-sm"
                          >
                            Remove 1hr
                          </button>
                          <button
                            onClick={() => handleAdjustHours(pkg.id, 1)}
                            className="bg-green-600 text-white px-3 py-2 rounded-lg hover:bg-green-700 text-sm"
                          >
                            Add 1hr
                          </button>
                          <button
                            onClick={() => handleAdjustHours(pkg.id, -0.5)}
                            className="bg-red-500 text-white px-3 py-2 rounded-lg hover:bg-red-600 text-sm"
                          >
                            Remove 30min
                          </button>
                          <button
                            onClick={() => handleAdjustHours(pkg.id, 0.5)}
                            className="bg-green-500 text-white px-3 py-2 rounded-lg hover:bg-green-600 text-sm"
                          >
                            Add 30min
                          </button>
                          <button
                            onClick={() => handleAdjustHours(pkg.id, -0.25)}
                            className="bg-red-400 text-white px-3 py-2 rounded-lg hover:bg-red-500 text-sm"
                          >
                            Remove 15min
                          </button>
                          <button
                            onClick={() => handleAdjustHours(pkg.id, 0.25)}
                            className="bg-green-400 text-white px-3 py-2 rounded-lg hover:bg-green-500 text-sm"
                          >
                            Add 15min
                          </button>
                          <button
                            onClick={() => {
                              setAdjustingPackage(null);
                              setAdjustmentReason('');
                            }}
                            className="px-3 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Homework & Resources Dropdowns */}
          <div className="space-y-4">
            {/* Homework Dropdown */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              <button
                onClick={() => setShowHomeworkDropdown(!showHomeworkDropdown)}
                className="w-full p-4 flex items-center justify-between hover:bg-gray-50"
              >
                <div className="flex items-center">
                  <BookOpen className="w-5 h-5 mr-2 text-gray-600" />
                  <span className="font-medium text-gray-900">Homework & Lesson Resources</span>
                  <span className="ml-2 text-sm text-gray-500">({homework.length + lessonResources.length})</span>
                </div>
                {showHomeworkDropdown ? (
                  <ChevronUp className="w-5 h-5 text-gray-400" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-gray-400" />
                )}
              </button>
              
              {showHomeworkDropdown && (
                <div className="border-t border-gray-200 p-4">
                  {homework.length === 0 && lessonResources.length === 0 ? (
                    <p className="text-gray-500 text-sm">No homework or lesson resources yet.</p>
                  ) : (
                    <div className="space-y-4">
                      {/* Homework Section */}
                      {homework.length > 0 && (
                        <div>
                          <h4 className="font-medium text-gray-900 mb-3">Homework Assignments</h4>
                          <div className="space-y-3">
                            {(showAllHomework ? homework : homework.slice(0, 5)).map((hw) => (
                              <div key={hw.id} className="border-l-4 border-primary-500 pl-3 py-2">
                                <div className="flex justify-between items-start">
                                  <div>
                                    <h5 className="font-medium text-gray-900">{hw.title}</h5>
                                    <p className="text-sm text-gray-600">{hw.subject.name}</p>
                                    {hw.description && (
                                      <p className="text-sm text-gray-500 mt-1">{hw.description}</p>
                                    )}
                                  </div>
                                  <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getStatusColor(hw.status)}`}>
                                    {hw.status}
                                  </span>
                                </div>
                                <p className="text-xs text-gray-500 mt-1">
                                  Due: {formatDate(hw.dueDate)}
                                </p>
                              </div>
                            ))}
                            {homework.length > 5 && (
                              <button 
                                onClick={() => setShowAllHomework(!showAllHomework)}
                                className="text-primary-600 hover:text-primary-800 text-sm text-center w-full py-2 hover:bg-primary-50 rounded transition-colors"
                              >
                                {showAllHomework ? 'Show less' : `+${homework.length - 5} more assignments`}
                              </button>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Lesson Resources Section */}
                      {lessonResources.length > 0 && (
                        <div>
                          <h4 className="font-medium text-gray-900 mb-3">Lesson Resources</h4>
                          <div className="space-y-2">
                            {(showAllResources ? lessonResources : lessonResources.slice(0, 5)).map((resource) => (
                              <div key={resource.id} className="border-l-4 border-blue-500 pl-3 py-2">
                                <div className="flex justify-between items-center">
                                  <div>
                                    <h5 className="font-medium text-gray-900">{resource.title}</h5>
                                    <p className="text-sm text-gray-600">{resource.fileExtension || 'File'}</p>
                                  </div>
                                  <a
                                    href={`/api/resources/${resource.id}/download`}
                                    className="text-primary-600 hover:text-primary-800 text-sm"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                  >
                                    Download
                                  </a>
                                </div>
                                <p className="text-xs text-gray-500 mt-1">
                                  Added: {formatDate(resource.createdAt)}
                                </p>
                              </div>
                            ))}
                            {lessonResources.length > 5 && (
                              <button 
                                onClick={() => setShowAllResources(!showAllResources)}
                                className="text-primary-600 hover:text-primary-800 text-sm text-center w-full py-2 hover:bg-primary-50 rounded transition-colors"
                              >
                                {showAllResources ? 'Show less' : `+${lessonResources.length - 5} more resources`}
                              </button>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

          </div>
        </div>

        {/* Right Column - 3/5 width - Lesson History */}
        <div className="lg:col-span-3">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                <Calendar className="w-5 h-5 mr-2 text-gray-600" />
                Lesson History
                <span className="ml-2 text-sm text-gray-500">({getFilteredBookings().length} shown)</span>
              </h2>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="showDeniedCancelled"
                  checked={showDeniedCancelled}
                  onChange={(e) => setShowDeniedCancelled(e.target.checked)}
                  className="mr-2"
                />
                <label htmlFor="showDeniedCancelled" className="text-sm text-gray-600">
                  Show denied/cancelled
                </label>
              </div>
            </div>

            {/* View tabs */}
            <div className="flex flex-wrap gap-1 mb-4 border-b border-gray-200">
              {([
                { id: 'list', label: 'List', icon: List },
                { id: 'subjects', label: 'Subjects', icon: BarChart3 },
                { id: 'calendar', label: 'Calendar', icon: CalendarDays },
                { id: 'trends', label: 'Trends', icon: TrendingUp },
              ] as const).map(tab => {
                const Icon = tab.icon;
                const active = historyView === tab.id;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setHistoryView(tab.id)}
                    className={`flex items-center px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                      active
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <Icon className="w-4 h-4 mr-1.5" />
                    {tab.label}
                  </button>
                );
              })}
            </div>

            {getFilteredBookings().length === 0 ? (
              <p className="text-gray-500 text-center py-8">
                {bookings.length === 0 ? 'No lessons booked yet.' : 'No lessons to show with current filter.'}
              </p>
            ) : historyView === 'list' ? (
              (() => {
                const subjectKey = (b: Booking) => `${b.subject.name}|${b.subject.category}`;
                const subjectOptions = Array.from(
                  new Map(
                    getFilteredBookings().map(b => [subjectKey(b), { key: subjectKey(b), label: `${b.subject.name} - ${formatSubjectCategory(b.subject.category)}` }])
                  ).values()
                ).sort((a, b) => a.label.localeCompare(b.label));

                const listFiltered = getFilteredBookings().filter(b => {
                  if (listSubjectFilter !== 'ALL' && subjectKey(b) !== listSubjectFilter) return false;
                  if (listModeFilter === 'ONLINE' && !b.isOnline) return false;
                  if (listModeFilter === 'IN_PERSON' && b.isOnline) return false;
                  if (listTypeFilter !== 'ALL' && b.type !== listTypeFilter) return false;
                  return true;
                });

                const anyFilterActive = listSubjectFilter !== 'ALL' || listModeFilter !== 'ALL' || listTypeFilter !== 'ALL';

                return (
                  <div className="space-y-3">
                    <div className="flex flex-wrap items-center gap-2 p-3 bg-gray-50 border border-gray-200 rounded-lg">
                      <select
                        value={listSubjectFilter}
                        onChange={(e) => setListSubjectFilter(e.target.value)}
                        className="text-sm border border-gray-300 rounded-md px-2 py-1.5 bg-white"
                      >
                        <option value="ALL">All subjects</option>
                        {subjectOptions.map(o => (
                          <option key={o.key} value={o.key}>{o.label}</option>
                        ))}
                      </select>

                      <select
                        value={listModeFilter}
                        onChange={(e) => setListModeFilter(e.target.value as 'ALL' | 'ONLINE' | 'IN_PERSON')}
                        className="text-sm border border-gray-300 rounded-md px-2 py-1.5 bg-white"
                      >
                        <option value="ALL">Online + In-Person</option>
                        <option value="ONLINE">Online only</option>
                        <option value="IN_PERSON">In-Person only</option>
                      </select>

                      <select
                        value={listTypeFilter}
                        onChange={(e) => setListTypeFilter(e.target.value as 'ALL' | 'LESSON' | 'CONSULTATION')}
                        className="text-sm border border-gray-300 rounded-md px-2 py-1.5 bg-white"
                      >
                        <option value="ALL">Lessons + Consultations</option>
                        <option value="LESSON">Lessons only</option>
                        <option value="CONSULTATION">Consultations only</option>
                      </select>

                      {anyFilterActive && (
                        <button
                          onClick={() => { setListSubjectFilter('ALL'); setListModeFilter('ALL'); setListTypeFilter('ALL'); }}
                          className="text-sm text-blue-600 hover:text-blue-800 underline ml-auto"
                        >
                          Clear filters
                        </button>
                      )}

                      <span className="text-xs text-gray-500 ml-auto">{listFiltered.length} match{listFiltered.length === 1 ? '' : 'es'}</span>
                    </div>

                    {listFiltered.length === 0 ? (
                      <p className="text-gray-500 text-center py-8">No lessons match the current filters.</p>
                    ) : (
                      <div className="space-y-3 max-h-[700px] overflow-y-auto">
                        {listFiltered.map((booking) => {
                  const duration = calculateLessonDuration(booking.startTime, booking.endTime);
                  const isPaid = booking.payments?.some(p => p.status === 'PAID');
                  const usedHours = booking.packageTransaction?.hoursChanged ? Math.abs(booking.packageTransaction.hoursChanged) : 0;
                  const isPending = booking.payments?.some(p => p.status === 'PENDING');
                  const isPaymentRequired = !isPaid && usedHours === 0 && !isPending;

                  return (
                    <div key={booking.id} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50">
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <h4 className="font-medium text-gray-900">
                            {booking.subject.name} - {formatSubjectCategory(booking.subject.category)}
                          </h4>
                          <p className="text-sm text-gray-600">
                            {formatDateTime(booking.startTime)}
                          </p>
                          <p className="text-sm text-gray-500">
                            Duration: {formatDuration(duration)} • {booking.isOnline ? 'Online' : 'In-Person'}
                          </p>
                        </div>
                        <div className="text-right">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(booking.status)}`}>
                            {getStatusIcon(booking.status)}
                            <span className="ml-1">{booking.status}</span>
                          </span>
                          <p className="text-lg font-semibold text-gray-900 mt-2">
                            £{booking.price.toFixed(2)}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100">
                        {usedHours > 0 ? (
                          <div className="flex items-center text-sm text-green-600">
                            <Package className="w-4 h-4 mr-1" />
                            Used {formatHours(usedHours)} package hours
                          </div>
                        ) : isPaid ? (
                          <div className="flex items-center text-sm text-green-600">
                            <CreditCard className="w-4 h-4 mr-1" />
                            Paid
                          </div>
                        ) : isPending ? (
                          <div className="flex items-center text-sm text-yellow-600">
                            <Clock className="w-4 h-4 mr-1" />
                            Payment Pending
                          </div>
                        ) : isPaymentRequired ? (
                          <div className="flex items-center text-sm text-red-600">
                            <AlertCircle className="w-4 h-4 mr-1" />
                            Payment Required
                          </div>
                        ) : (
                          <div className="flex items-center text-sm text-gray-600">
                            <CheckCircle className="w-4 h-4 mr-1" />
                            Completed
                          </div>
                        )}

                        <span className="text-xs text-gray-500">
                          {booking.type === 'CONSULTATION' ? 'Consultation' : 'Lesson'}
                        </span>
                      </div>
                    </div>
                  );
                })}
                      </div>
                    )}
                  </div>
                );
              })()
            ) : historyView === 'subjects' ? (
              (() => {
                const totals = getFilteredBookings().reduce((acc, b) => {
                  const mode: 'Online' | 'In-Person' = b.isOnline ? 'Online' : 'In-Person';
                  const key = `${b.subject.name}|${b.subject.category}|${mode}`;
                  const duration = calculateLessonDuration(b.startTime, b.endTime);
                  if (!acc[key]) acc[key] = { name: b.subject.name, category: b.subject.category, mode, hours: 0, lessons: 0, spent: 0 };
                  acc[key].hours += duration;
                  acc[key].lessons += 1;
                  acc[key].spent += b.price;
                  return acc;
                }, {} as Record<string, { name: string; category: string; mode: 'Online' | 'In-Person'; hours: number; lessons: number; spent: number }>);
                const entries = Object.values(totals).sort((a, b) => b.hours - a.hours);
                const maxHours = entries[0]?.hours || 1;
                const grandHours = entries.reduce((s, e) => s + e.hours, 0);
                const grandSpent = entries.reduce((s, e) => s + e.spent, 0);
                const grandLessons = entries.reduce((s, e) => s + e.lessons, 0);

                return (
                  <div className="space-y-4">
                    <div className="grid grid-cols-3 gap-3">
                      <div className="bg-blue-50 border border-blue-100 rounded-lg p-3">
                        <p className="text-xs uppercase tracking-wide text-blue-700 font-semibold">Total hours</p>
                        <p className="text-2xl font-bold text-blue-900 mt-1">{grandHours.toFixed(1)}</p>
                      </div>
                      <div className="bg-emerald-50 border border-emerald-100 rounded-lg p-3">
                        <p className="text-xs uppercase tracking-wide text-emerald-700 font-semibold">Total lessons</p>
                        <p className="text-2xl font-bold text-emerald-900 mt-1">{grandLessons}</p>
                      </div>
                      <div className="bg-amber-50 border border-amber-100 rounded-lg p-3">
                        <p className="text-xs uppercase tracking-wide text-amber-700 font-semibold">Total spent</p>
                        <p className="text-2xl font-bold text-amber-900 mt-1">£{grandSpent.toFixed(0)}</p>
                      </div>
                    </div>

                    <p className="text-xs text-gray-500">Each row is one subject + delivery mode combination, across all time.</p>

                    <div className="space-y-3 max-h-[700px] overflow-y-auto">
                      {entries.map(e => {
                        const widthPct = (e.hours / maxHours) * 100;
                        const isOnline = e.mode === 'Online';
                        return (
                          <div key={`${e.name}|${e.category}|${e.mode}`} className="border border-gray-200 rounded-lg p-4">
                            <div className="flex justify-between items-baseline mb-2 gap-3">
                              <div className="min-w-0">
                                <h4 className="font-medium text-gray-900 truncate">
                                  {e.name} <span className="text-gray-500">- {formatSubjectCategory(e.category)}</span>
                                </h4>
                                <span className={`inline-block mt-1 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide ${
                                  isOnline ? 'bg-blue-100 text-blue-700' : 'bg-orange-100 text-orange-700'
                                }`}>
                                  {e.mode}
                                </span>
                              </div>
                              <div className="text-right shrink-0">
                                <p className="text-lg font-semibold text-blue-700">{formatDuration(e.hours)}</p>
                                <p className="text-xs text-gray-500">{e.lessons} lesson{e.lessons === 1 ? '' : 's'} • £{e.spent.toFixed(2)}</p>
                              </div>
                            </div>
                            <div className="w-full bg-gray-100 rounded-full h-3 overflow-hidden">
                              <div
                                className={`h-3 rounded-full transition-all ${isOnline ? 'bg-blue-500' : 'bg-orange-400'}`}
                                style={{ width: `${widthPct}%` }}
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })()
            ) : historyView === 'calendar' ? (
              (() => {
                const lessonsByDate = getFilteredBookings().reduce((acc, b) => {
                  const d = new Date(b.startTime);
                  const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
                  if (!acc[key]) acc[key] = [];
                  acc[key].push(b);
                  return acc;
                }, {} as Record<string, Booking[]>);

                const today = new Date();
                const endRef = new Date(today.getFullYear(), today.getMonth() - calendarOffset, 1);
                const months = [3, 2, 1, 0].map(i => new Date(endRef.getFullYear(), endRef.getMonth() - i, 1));
                const dayLabels = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
                const monthLabel = (d: Date) => d.toLocaleString('en-US', { month: 'long', year: 'numeric' });
                const rangeLabel = `${months[0].toLocaleString('en-US', { month: 'short', year: 'numeric' })} – ${months[3].toLocaleString('en-US', { month: 'short', year: 'numeric' })}`;
                const selectedLessons = selectedDate ? (lessonsByDate[selectedDate] || []) : [];
                const selectedDateLabel = selectedDate
                  ? (() => {
                      const [y, m, d] = selectedDate.split('-').map(Number);
                      return new Date(y, m, d).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
                    })()
                  : '';

                return (
                  <div className="space-y-4 max-h-[800px] overflow-y-auto">
                    <div className="flex items-center justify-between gap-2">
                      <button
                        onClick={() => { setCalendarOffset(o => o + 4); setSelectedDate(null); }}
                        className="px-3 py-1.5 text-sm border border-gray-300 rounded-md hover:bg-gray-50 text-gray-700"
                      >
                        ← Earlier
                      </button>
                      <span className="text-sm font-medium text-gray-700">{rangeLabel}</span>
                      <button
                        onClick={() => { setCalendarOffset(o => Math.max(0, o - 4)); setSelectedDate(null); }}
                        disabled={calendarOffset === 0}
                        className="px-3 py-1.5 text-sm border border-gray-300 rounded-md hover:bg-gray-50 text-gray-700 disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        Later →
                      </button>
                    </div>

                    <div className="flex items-center justify-end gap-4 text-xs text-gray-600">
                      <div className="flex items-center gap-1.5">
                        <span className="inline-block w-3 h-3 rounded-full bg-blue-500" />
                        <span>1 lesson</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <span className="inline-block w-3 h-3 rounded-full bg-blue-600 ring-2 ring-blue-200" />
                        <span>2+ lessons</span>
                      </div>
                      <span className="text-gray-400">Click a day for details</span>
                    </div>

                    {selectedDate && selectedLessons.length > 0 && (
                      <div className="border border-blue-200 bg-blue-50 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-3">
                          <h4 className="text-sm font-semibold text-blue-900">{selectedDateLabel} — {selectedLessons.length} lesson{selectedLessons.length === 1 ? '' : 's'}</h4>
                          <button
                            onClick={() => setSelectedDate(null)}
                            className="text-blue-700 hover:text-blue-900"
                            aria-label="Close"
                          >
                            <XIcon className="w-4 h-4" />
                          </button>
                        </div>
                        <div className="space-y-2">
                          {selectedLessons
                            .slice()
                            .sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime())
                            .map(l => {
                              const time = new Date(l.startTime).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
                              const dur = calculateLessonDuration(l.startTime, l.endTime);
                              return (
                                <div key={l.id} className="bg-white border border-blue-100 rounded-md p-3 flex justify-between items-start gap-3">
                                  <div className="min-w-0">
                                    <p className="font-medium text-gray-900">{l.subject.name} <span className="text-gray-500">- {formatSubjectCategory(l.subject.category)}</span></p>
                                    <p className="text-sm text-gray-600 mt-0.5">
                                      {time} • {formatDuration(dur)} •{' '}
                                      <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase ${
                                        l.isOnline ? 'bg-blue-100 text-blue-700' : 'bg-orange-100 text-orange-700'
                                      }`}>
                                        {l.isOnline ? 'Online' : 'In-Person'}
                                      </span>
                                    </p>
                                    <p className="text-xs text-gray-500 mt-0.5">{l.type === 'CONSULTATION' ? 'Consultation' : 'Lesson'}</p>
                                  </div>
                                  <div className="text-right shrink-0">
                                    <p className="font-semibold text-gray-900">£{l.price.toFixed(2)}</p>
                                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium mt-1 ${getStatusColor(l.status)}`}>
                                      {l.status}
                                    </span>
                                  </div>
                                </div>
                              );
                            })}
                        </div>
                      </div>
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {months.map(month => {
                        const year = month.getFullYear();
                        const m = month.getMonth();
                        const firstDay = new Date(year, m, 1).getDay();
                        const daysInMonth = new Date(year, m + 1, 0).getDate();
                        const cells: (number | null)[] = [];
                        for (let i = 0; i < firstDay; i++) cells.push(null);
                        for (let d = 1; d <= daysInMonth; d++) cells.push(d);
                        while (cells.length % 7 !== 0) cells.push(null);

                        return (
                          <div key={`${year}-${m}`} className="border border-gray-200 rounded-lg p-4">
                            <h4 className="text-sm font-semibold text-gray-900 mb-3 text-center">{monthLabel(month)}</h4>
                            <div className="grid grid-cols-7 gap-1 mb-1">
                              {dayLabels.map((l, i) => (
                                <div key={i} className="text-[10px] text-gray-400 text-center font-medium uppercase">{l}</div>
                              ))}
                            </div>
                            <div className="grid grid-cols-7 gap-1">
                              {cells.map((day, i) => {
                                if (day === null) return <div key={i} className="aspect-square" />;
                                const key = `${year}-${m}-${day}`;
                                const lessons = lessonsByDate[key] || [];
                                const count = lessons.length;
                                const isToday = day === today.getDate() && m === today.getMonth() && year === today.getFullYear();
                                const isSelected = selectedDate === key;

                                if (count === 0) {
                                  return (
                                    <div
                                      key={i}
                                      className={`aspect-square flex items-center justify-center text-xs ${
                                        isToday ? 'text-blue-600 font-semibold ring-1 ring-blue-200 rounded-full' : 'text-gray-400'
                                      }`}
                                    >
                                      {day}
                                    </div>
                                  );
                                }

                                return (
                                  <button
                                    key={i}
                                    type="button"
                                    onClick={() => setSelectedDate(isSelected ? null : key)}
                                    className="aspect-square flex items-center justify-center relative cursor-pointer focus:outline-none"
                                    aria-label={`${count} lesson${count === 1 ? '' : 's'} on ${monthLabel(month)} ${day}`}
                                  >
                                    <div
                                      className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold text-white shadow-sm transition-all hover:scale-110 ${
                                        count >= 2 ? 'bg-blue-600 ring-2 ring-blue-200' : 'bg-blue-500'
                                      } ${isToday ? 'ring-2 ring-blue-900' : ''} ${isSelected ? 'ring-4 ring-blue-400 scale-110' : ''}`}
                                    >
                                      {day}
                                    </div>
                                    {count >= 2 && (
                                      <span className="absolute -top-0.5 -right-0.5 bg-white text-blue-700 text-[9px] font-bold rounded-full w-4 h-4 flex items-center justify-center border border-blue-300">
                                        {count}
                                      </span>
                                    )}
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })()
            ) : (
              (() => {
                const monthly = getFilteredBookings().reduce((acc, b) => {
                  const d = new Date(b.startTime);
                  const key = `${d.getFullYear()}-${String(d.getMonth()).padStart(2, '0')}`;
                  if (!acc[key]) acc[key] = { key, year: d.getFullYear(), month: d.getMonth(), hours: 0, lessons: 0, spent: 0, online: 0, inPerson: 0 };
                  const duration = calculateLessonDuration(b.startTime, b.endTime);
                  acc[key].hours += duration;
                  acc[key].lessons += 1;
                  acc[key].spent += b.price;
                  if (b.isOnline) acc[key].online += duration; else acc[key].inPerson += duration;
                  return acc;
                }, {} as Record<string, { key: string; year: number; month: number; hours: number; lessons: number; spent: number; online: number; inPerson: number }>);

                const today = new Date();
                const recentMonths: { key: string; year: number; month: number; hours: number; lessons: number; spent: number; online: number; inPerson: number }[] = [];
                for (let i = 5; i >= 0; i--) {
                  const d = new Date(today.getFullYear(), today.getMonth() - (i + trendsOffset), 1);
                  const key = `${d.getFullYear()}-${String(d.getMonth()).padStart(2, '0')}`;
                  recentMonths.push(monthly[key] || { key, year: d.getFullYear(), month: d.getMonth(), hours: 0, lessons: 0, spent: 0, online: 0, inPerson: 0 });
                }
                const maxHours = Math.max(...recentMonths.map(m => m.hours), 1);
                const monthShort = (year: number, month: number) =>
                  new Date(year, month, 1).toLocaleString('en-US', { month: 'short' });

                const totalRecentHours = recentMonths.reduce((s, m) => s + m.hours, 0);
                const totalRecentSpent = recentMonths.reduce((s, m) => s + m.spent, 0);
                const totalRecentLessons = recentMonths.reduce((s, m) => s + m.lessons, 0);
                const avgHoursPerLesson = totalRecentLessons > 0 ? totalRecentHours / totalRecentLessons : 0;

                const trendsRangeLabel = `${monthShort(recentMonths[0].year, recentMonths[0].month)} ${recentMonths[0].year} – ${monthShort(recentMonths[5].year, recentMonths[5].month)} ${recentMonths[5].year}`;

                return (
                  <div className="space-y-5">
                    <div className="flex items-center justify-between gap-2">
                      <button
                        onClick={() => setTrendsOffset(o => o + 6)}
                        className="px-3 py-1.5 text-sm border border-gray-300 rounded-md hover:bg-gray-50 text-gray-700"
                      >
                        ← Earlier
                      </button>
                      <span className="text-sm font-medium text-gray-700">{trendsRangeLabel}</span>
                      <button
                        onClick={() => setTrendsOffset(o => Math.max(0, o - 6))}
                        disabled={trendsOffset === 0}
                        className="px-3 py-1.5 text-sm border border-gray-300 rounded-md hover:bg-gray-50 text-gray-700 disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        Later →
                      </button>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      <div className="bg-blue-50 border border-blue-100 rounded-lg p-3">
                        <p className="text-[10px] uppercase tracking-wide text-blue-700 font-semibold">6 mo hours</p>
                        <p className="text-xl font-bold text-blue-900 mt-1">{totalRecentHours.toFixed(1)}</p>
                      </div>
                      <div className="bg-emerald-50 border border-emerald-100 rounded-lg p-3">
                        <p className="text-[10px] uppercase tracking-wide text-emerald-700 font-semibold">Lessons</p>
                        <p className="text-xl font-bold text-emerald-900 mt-1">{totalRecentLessons}</p>
                      </div>
                      <div className="bg-amber-50 border border-amber-100 rounded-lg p-3">
                        <p className="text-[10px] uppercase tracking-wide text-amber-700 font-semibold">Spent</p>
                        <p className="text-xl font-bold text-amber-900 mt-1">£{totalRecentSpent.toFixed(0)}</p>
                      </div>
                      <div className="bg-purple-50 border border-purple-100 rounded-lg p-3">
                        <p className="text-[10px] uppercase tracking-wide text-purple-700 font-semibold">Avg / lesson</p>
                        <p className="text-xl font-bold text-purple-900 mt-1">{avgHoursPerLesson.toFixed(1)}h</p>
                      </div>
                    </div>

                    <div className="border border-gray-200 rounded-lg p-4">
                      <h4 className="text-sm font-semibold text-gray-900 mb-4">Hours per month (last 6)</h4>
                      <div className="flex items-end justify-between gap-2 h-48">
                        {recentMonths.map(m => {
                          const heightPct = (m.hours / maxHours) * 100;
                          const onlineHeight = m.hours > 0 ? (m.online / m.hours) * heightPct : 0;
                          const inPersonHeight = heightPct - onlineHeight;
                          return (
                            <div key={m.key} className="flex-1 flex flex-col items-center justify-end h-full">
                              <span className="text-xs font-semibold text-gray-700 mb-1">
                                {m.hours > 0 ? `${m.hours.toFixed(1)}h` : ''}
                              </span>
                              <div className="w-full max-w-[48px] bg-gray-100 rounded-t-md overflow-hidden flex flex-col-reverse" style={{ height: `${Math.max(heightPct, 2)}%` }}>
                                <div className="bg-blue-500 w-full" style={{ height: `${onlineHeight / Math.max(heightPct, 0.0001) * 100}%` }} />
                                <div className="bg-blue-300 w-full" style={{ height: `${inPersonHeight / Math.max(heightPct, 0.0001) * 100}%` }} />
                              </div>
                              <span className="text-xs text-gray-500 mt-1">{monthShort(m.year, m.month)}</span>
                            </div>
                          );
                        })}
                      </div>
                      <div className="flex items-center justify-center gap-4 mt-3 text-xs text-gray-600">
                        <div className="flex items-center gap-1.5">
                          <span className="inline-block w-3 h-3 rounded bg-blue-500" />
                          <span>Online</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <span className="inline-block w-3 h-3 rounded bg-blue-300" />
                          <span>In-Person</span>
                        </div>
                      </div>
                    </div>

                    <div className="border border-gray-200 rounded-lg overflow-hidden">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
                          <tr>
                            <th className="text-left px-4 py-2 font-semibold">Month</th>
                            <th className="text-right px-4 py-2 font-semibold">Lessons</th>
                            <th className="text-right px-4 py-2 font-semibold">Hours</th>
                            <th className="text-right px-4 py-2 font-semibold">Spent</th>
                          </tr>
                        </thead>
                        <tbody>
                          {[...recentMonths].reverse().map(m => (
                            <tr key={m.key} className="border-t border-gray-100">
                              <td className="px-4 py-2 text-gray-900">{monthShort(m.year, m.month)} {m.year}</td>
                              <td className="px-4 py-2 text-right text-gray-700">{m.lessons}</td>
                              <td className="px-4 py-2 text-right text-gray-700">{formatDuration(m.hours)}</td>
                              <td className="px-4 py-2 text-right text-gray-700">£{m.spent.toFixed(2)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                );
              })()
            )}
          </div>
        </div>
      </div>

      {editOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          role="dialog"
          aria-modal="true"
          onClick={() => !editSaving && setEditOpen(false)}
        >
          <div
            className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-start p-6 border-b">
              <div>
                <h2 className="text-xl font-bold text-gray-900">Edit student</h2>
                <p className="text-sm text-gray-600 mt-1">Name and subjects update everywhere this student appears.</p>
              </div>
              <button
                type="button"
                onClick={() => !editSaving && setEditOpen(false)}
                disabled={editSaving}
                className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
                aria-label="Close"
              >
                <XIcon className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {editError && (
                <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">
                  {editError}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Name</label>
                <input
                  type="text"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                  maxLength={100}
                  disabled={editSaving}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Subjects ({editSubjectIds.size} selected)
                </label>
                {allSubjects.length === 0 ? (
                  <p className="text-sm text-gray-500">Loading subjects…</p>
                ) : (
                  <div className="space-y-4 max-h-[40vh] overflow-y-auto border border-gray-200 rounded-md p-3">
                    {Object.keys(subjectsByCategory)
                      .sort()
                      .map((cat) => (
                        <div key={cat}>
                          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">
                            {categoryLabel(cat)}
                          </p>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                            {subjectsByCategory[cat].map((s) => {
                              const checked = editSubjectIds.has(s.id);
                              return (
                                <label
                                  key={s.id}
                                  className={`flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer text-sm transition-colors ${
                                    checked ? 'bg-primary-50 text-primary-900' : 'hover:bg-gray-50'
                                  }`}
                                >
                                  <input
                                    type="checkbox"
                                    checked={checked}
                                    onChange={(e) => {
                                      setEditSubjectIds((prev) => {
                                        const next = new Set(prev);
                                        if (e.target.checked) next.add(s.id); else next.delete(s.id);
                                        return next;
                                      });
                                    }}
                                    disabled={editSaving}
                                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                                  />
                                  {s.name}
                                </label>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                  </div>
                )}
              </div>
            </div>

            <div className="flex justify-end gap-2 p-6 border-t bg-gray-50 rounded-b-lg">
              <button
                type="button"
                onClick={() => setEditOpen(false)}
                disabled={editSaving}
                className="btn btn-outline"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={saveEdit}
                disabled={editSaving}
                className="btn btn-primary"
              >
                {editSaving ? 'Saving…' : 'Save changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Preview Exact Modal (read-only) */}
      {previewExactPkg && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={closePreviewExact}
        >
          <div
            className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <Calculator className="w-5 h-5 text-indigo-600" />
                  Preview Exact Balance
                </h3>
                <p className="text-sm text-gray-500 mt-1">
                  {previewExactPkg.subject.name} &middot;{' '}
                  {previewExactPkg.totalHours}h Package
                </p>
                <p className="text-xs text-gray-400 mt-1">
                  Read-only. Nothing is saved — this just shows what the balance should be based on
                  actual lesson durations.
                </p>
              </div>
              <button
                onClick={closePreviewExact}
                className="text-gray-400 hover:text-gray-600"
                aria-label="Close"
              >
                <XIcon className="w-5 h-5" />
              </button>
            </div>

            <div className="px-6 py-4 overflow-y-auto">
              {previewExactLoading && <div className="text-sm text-gray-500">Calculating…</div>}
              {previewExactError && <div className="text-sm text-red-600">{previewExactError}</div>}
              {previewExactData && (
                <>
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <div className="border border-gray-200 rounded-lg p-4">
                      <div className="text-xs uppercase tracking-wide text-gray-500 mb-1">
                        Stored (current)
                      </div>
                      <div className="text-2xl font-semibold text-gray-900">
                        {previewExactData.stored.remainingHours.toFixed(2)} hrs
                      </div>
                      <div className="text-sm text-gray-500 mt-1">
                        remaining of {previewExactData.totalHours} hrs
                      </div>
                      <div className="text-sm text-gray-500">
                        Used: {previewExactData.stored.usedHours.toFixed(2)} hrs
                      </div>
                    </div>
                    <div className="border border-indigo-200 bg-indigo-50 rounded-lg p-4">
                      <div className="text-xs uppercase tracking-wide text-indigo-600 mb-1">
                        Exact (from lesson times)
                      </div>
                      <div className="text-2xl font-semibold text-indigo-900">
                        {previewExactData.exact.remainingHours.toFixed(2)} hrs
                      </div>
                      <div className="text-sm text-indigo-700 mt-1">
                        remaining of {previewExactData.totalHours} hrs
                      </div>
                      <div className="text-sm text-indigo-700">
                        Used: {previewExactData.exact.usedHours.toFixed(2)} hrs
                        {previewExactData.exact.adminAdjustmentHours !== 0 && (
                          <>
                            {' '}
                            (incl. {(-previewExactData.exact.adminAdjustmentHours).toFixed(2)} hrs
                            from admin adjustments)
                          </>
                        )}
                      </div>
                    </div>
                  </div>

                  <div
                    className="mb-6 p-3 rounded-md border text-sm flex items-start gap-2"
                    style={
                      previewExactData.discrepancy.remainingHours === 0
                        ? { borderColor: '#bbf7d0', backgroundColor: '#f0fdf4', color: '#166534' }
                        : { borderColor: '#fde68a', backgroundColor: '#fffbeb', color: '#92400e' }
                    }
                  >
                    {previewExactData.discrepancy.remainingHours === 0 ? (
                      <>
                        <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                        <span>Stored balance matches the exact calculation. No discrepancy.</span>
                      </>
                    ) : (
                      <>
                        <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                        <span>
                          Discrepancy: stored remaining differs from exact by{' '}
                          <strong>
                            {previewExactData.discrepancy.remainingHours > 0 ? '+' : ''}
                            {previewExactData.discrepancy.remainingHours.toFixed(2)} hrs
                          </strong>
                          . (Exact remaining is{' '}
                          {previewExactData.discrepancy.remainingHours > 0 ? 'higher' : 'lower'}.)
                        </span>
                      </>
                    )}
                  </div>

                  <div className="mb-2 text-sm font-medium text-gray-900">
                    Lessons booked against this package ({previewExactData.lessons.length})
                  </div>
                  {previewExactData.lessons.length === 0 ? (
                    <div className="text-sm text-gray-500 mb-4">
                      No lessons recorded against this package.
                    </div>
                  ) : (
                    <div className="overflow-x-auto mb-4">
                      <table className="min-w-full divide-y divide-gray-200 text-sm">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                              When
                            </th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                              Status
                            </th>
                            <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                              Actual hrs
                            </th>
                            <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                              Recorded
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {previewExactData.lessons.map((l) => {
                            const recordedUsed = -l.recordedHoursChanged;
                            const mismatch = Math.abs(recordedUsed - l.actualHours) > 0.005;
                            return (
                              <tr key={l.transactionId}>
                                <td className="px-3 py-2 text-gray-900">
                                  {new Date(l.startTime).toLocaleString('en-GB', {
                                    day: '2-digit',
                                    month: 'short',
                                    year: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit'
                                  })}
                                </td>
                                <td className="px-3 py-2 text-gray-600">{l.status}</td>
                                <td className="px-3 py-2 text-right text-gray-900">
                                  {l.actualHours.toFixed(2)}
                                </td>
                                <td
                                  className={`px-3 py-2 text-right ${
                                    mismatch ? 'text-amber-700 font-medium' : 'text-gray-600'
                                  }`}
                                >
                                  {recordedUsed.toFixed(2)}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {previewExactData.adminAdjustments.length > 0 && (
                    <>
                      <div className="mb-2 text-sm font-medium text-gray-900">
                        Admin adjustments ({previewExactData.adminAdjustments.length})
                      </div>
                      <div className="overflow-x-auto mb-2">
                        <table className="min-w-full divide-y divide-gray-200 text-sm">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                                Date
                              </th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                                Reason
                              </th>
                              <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                                Hours
                              </th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100">
                            {previewExactData.adminAdjustments.map((a) => (
                              <tr key={a.transactionId}>
                                <td className="px-3 py-2 text-gray-900">
                                  {new Date(a.createdAt).toLocaleDateString('en-GB')}
                                </td>
                                <td className="px-3 py-2 text-gray-600">{a.description || '—'}</td>
                                <td
                                  className={`px-3 py-2 text-right ${
                                    a.hoursChanged < 0 ? 'text-red-600' : 'text-green-600'
                                  }`}
                                >
                                  {a.hoursChanged > 0 ? '+' : ''}
                                  {a.hoursChanged.toFixed(2)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </>
                  )}
                </>
              )}
            </div>

            <div className="px-6 py-3 border-t border-gray-200 bg-gray-50 flex justify-end">
              <button
                onClick={closePreviewExact}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StudentDetailPage;